// Site-wide search index for /search.
// Combines curated resource/service pages with blog posts loaded from
// src/content/posts/*.json. Kept fully client-safe so the search page can
// filter results synchronously without a server round-trip.

import { listPosts } from "@/lib/posts";

export type SearchCategory =
  | "Service"
  | "Resource"
  | "Blog"
  | "About"
  | "Contact";

export type SearchItem = {
  title: string;
  url: string;
  description: string;
  category: SearchCategory;
  date?: string;
  keywords?: string[];
};

const curated: SearchItem[] = [
  // Top-level
  { title: "Home", url: "/", category: "About", description: "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice." },
  { title: "About", url: "/about", category: "About", description: "About Dr. Matthew Fitzer and Virginia Bariatric Surgery." },
  { title: "Our Team", url: "/team", category: "About", description: "Meet the surgeon, nurses, and staff at Virginia Bariatric Surgery." },
  { title: "Our Program", url: "/our-program", category: "About", description: "Our comprehensive weight-loss surgery program.", keywords: ["comprehensive", "journey"] },
  
  { title: "Contact / Appointments", url: "/contact", category: "Contact", description: "Request an appointment or message the office.", keywords: ["appointment", "message", "phone"] },
  { title: "Before & After Results", url: "/results", category: "About", description: "Patient before-and-after weight-loss stories." },

  // Services
  { title: "Weight-Loss Services", url: "/services", category: "Service", description: "Overview of weight-loss surgery and non-surgical options.", keywords: ["surgery", "options"] },
  { title: "Sleeve Gastrectomy", url: "/services/sleeve", category: "Service", description: "Laparoscopic sleeve gastrectomy surgery with Dr. Fitzer.", keywords: ["vsg", "gastric sleeve"] },
  { title: "Gastric Bypass (Roux-en-Y)", url: "/services/bypass", category: "Service", description: "Roux-en-Y gastric bypass for weight loss and diabetes.", keywords: ["rny", "roux en y"] },
  { title: "Adjustable Gastric Band", url: "/services/band", category: "Service", description: "Adjustable gastric band procedure and follow-up.", keywords: ["lap band"] },
  { title: "Orbera® Gastric Balloon", url: "/services/balloon", category: "Service", description: "Orbera intragastric balloon — a non-surgical weight-loss option.", keywords: ["intragastric balloon", "orbera"] },
  { title: "Endoscopic Sleeve Gastroplasty (ESG)", url: "/services/esg", category: "Service", description: "Incisionless endoscopic sleeve gastroplasty in the DC metro." },
  { title: "Medical Weight Loss", url: "/medical-weight-loss", category: "Service", description: "Physician-supervised, non-surgical weight loss with Dr. Fitzer.", keywords: ["medical weight loss", "non-surgical", "glp-1"] },
  { title: "Weight-Loss Medication Insurance Coverage in Virginia", url: "/medical-weight-loss/insurance", category: "Service", description: "How Virginia insurers decide coverage for Zepbound, Wegovy, and Saxenda — the two gates, the diagnosis backdoor, and a plan-by-plan comparison.", keywords: ["insurance", "coverage", "zepbound", "wegovy", "saxenda", "prior authorization"] },
  { title: "Virginia Medicaid & State Employee (COVA) GLP-1 Coverage", url: "/medical-weight-loss/insurance/medicaid-cova", category: "Service", description: "Cardinal Care BMI rules and the July 2026 COVA change requiring a BMI of 35.", keywords: ["medicaid", "cardinal care", "cova", "state employee", "glp-1", "insurance"] },
  { title: "Medicare, TRICARE & FEHB Weight-Loss Drug Coverage", url: "/medical-weight-loss/insurance/medicare-tricare-fehb", category: "Service", description: "The $50 Medicare GLP-1 Bridge, TRICARE step therapy, and FEHB requirements.", keywords: ["medicare", "glp-1 bridge", "tricare", "fehb", "federal employees", "insurance"] },
  { title: "Employer, Commercial & Marketplace GLP-1 Coverage in Virginia", url: "/medical-weight-loss/insurance/commercial-marketplace", category: "Service", description: "Sentara, UnitedHealthcare, Cigna, Anthem, Aetna, CareFirst, Kaiser, and PBM formularies.", keywords: ["sentara", "unitedhealthcare", "cigna", "anthem", "aetna", "marketplace", "pbm", "insurance"] },
  { title: "GLP-1 Denials and Cash-Pay Pricing in Virginia", url: "/medical-weight-loss/insurance/cash-pay-denials", category: "Service", description: "Appealing a denial and current LillyDirect and NovoCare self-pay prices.", keywords: ["denial", "appeal", "cash pay", "self pay", "lillydirect", "novocare", "cost"] },
  { title: "Weight Loss Medications: Introduction", url: "/medical-weight-loss/medications", category: "Service", description: "GLP-1 and injectable weight-loss medication management.", keywords: ["glp-1", "semaglutide", "tirzepatide", "ozempic", "wegovy", "mounjaro", "zepbound"] },

  { title: "Revision Surgery", url: "/services/revisions", category: "Service", description: "Revision surgery for prior bariatric procedures.", keywords: ["revision", "redo"] },
  { title: "Gastric Sleeve Revision", url: "/services/revisions/sleeve", category: "Service", description: "Sleeve-to-bypass conversion, re-sleeve, and SADI/duodenal switch after a sleeve gastrectomy.", keywords: ["sleeve revision", "resleeve", "sleeve to bypass", "SADI"] },
  { title: "Gastric Bypass Revision", url: "/services/revisions/gastric-bypass", category: "Service", description: "TORe, surgical pouch revision, and distalization after a Roux-en-Y gastric bypass.", keywords: ["bypass revision", "TORe", "distalization", "pouch revision"] },
  { title: "LAP-BAND Removal & Revision", url: "/services/revisions/lap-band", category: "Service", description: "Band removal and conversion to a sleeve gastrectomy or gastric bypass.", keywords: ["lap-band removal", "band to sleeve", "band to bypass", "slipped band"] },

  // Resources
  { title: "Patient Resources", url: "/resources", category: "Resource", description: "Hub of patient resources: FAQ, forms, nutrition, financing, and more." },
  { title: "FAQ", url: "/resources/faq", category: "Resource", description: "Frequently asked questions about bariatric surgery." },
  { title: "Bariatric Surgery Requirements (Qualifications)", url: "/resources/qualifications", category: "Resource", description: "Do you qualify for weight-loss surgery? BMI and eligibility criteria.", keywords: ["bmi", "eligibility", "qualify"] },
  { title: "Insurance Coverage", url: "/resources/insurance-coverage", category: "Resource", description: "How Virginia Bariatric handles insurance pre-authorization." },
  { title: "New Patient Forms", url: "/resources/forms", category: "Resource", description: "Downloadable new-patient forms." },
  { title: "Instructions", url: "/resources/instructions", category: "Resource", description: "Pre-op and post-op instructions." },
  { title: "Nutrition", url: "/resources/nutrition", category: "Resource", description: "Bariatric nutrition guides and post-op diet stages." },
  { title: "Obesity and Diabetes", url: "/resources/obesity-and-diabetes", category: "Resource", description: "How obesity and type-2 diabetes are connected." },
  { title: "Financing & Payment Plans", url: "/resources/prosper", category: "Resource", description: "Ways to finance weight-loss surgery and spread payments over time.", keywords: ["financing", "payment plans", "loan"] },
  { title: "Support Groups", url: "/resources/support", category: "Resource", description: "Support-group calendar and community." },
  { title: "Suggestion Box", url: "/resources/suggestion", category: "Resource", description: "Send us a private suggestion." },
];

let cachedIndex: SearchItem[] | null = null;

export function getSearchIndex(): SearchItem[] {
  if (cachedIndex) return cachedIndex;
  const posts = listPosts().map<SearchItem>((p) => ({
    title: p.title,
    url: `/dr-fitzer-dissects-the-news/${p.slug}`,
    description: p.seoDescription || stripHtml(p.excerpt).slice(0, 220),
    category: "Blog",
    date: p.date,
    keywords: [p.subtitle ?? ""].filter(Boolean),
  }));
  cachedIndex = [...curated, ...posts];
  return cachedIndex;
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

function normalize(s: string): string {
  return s.toLowerCase();
}

export function searchItems(
  items: SearchItem[],
  query: string,
  category: "All" | SearchCategory,
): SearchItem[] {
  const q = normalize(query.trim());
  const filtered = items.filter((it) => {
    if (category !== "All" && it.category !== category) return false;
    if (!q) return true;
    const haystack = normalize(
      [it.title, it.description, it.category, ...(it.keywords ?? [])].join(" "),
    );
    // Match all whitespace-separated tokens.
    return q.split(/\s+/).every((tok) => haystack.includes(tok));
  });

  if (!q) {
    // Deterministic order: blogs newest-first at bottom, curated first.
    return filtered;
  }

  // Rank: title match > keyword match > description match.
  return filtered
    .map((it) => {
      const title = normalize(it.title);
      const kw = normalize((it.keywords ?? []).join(" "));
      const desc = normalize(it.description);
      let score = 0;
      for (const tok of q.split(/\s+/)) {
        if (title.includes(tok)) score += 5;
        if (title.startsWith(tok)) score += 3;
        if (kw.includes(tok)) score += 3;
        if (desc.includes(tok)) score += 1;
      }
      return { it, score };
    })
    .sort((a, b) => b.score - a.score)
    .map((x) => x.it);
}

export const CATEGORIES: readonly ("All" | SearchCategory)[] = [
  "All",
  "Service",
  "Resource",
  "Blog",
  "About",
  "Contact",
] as const;
